// File: frontend/src/pages/SuperAdmin/components/SystemSettings/LanguageManagement.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { settingsService } from '../../../../../services/superAdmin/settingsService';
import SuperAdminTableV2 from '../../common/SuperAdminTableV2';
import SuperAdminSearch from '../../common/SuperAdminSearch';
import styles from './LanguageManagement.module.css';

const LanguageManagement = ({ loading, setLoading, error, setError, success, setSuccess }) => {
  const { t } = useLanguage();
  
  // Language Management
  const [languageStrings, setLanguageStrings] = useState([]);
  const [filteredLanguageStrings, setFilteredLanguageStrings] = useState([]);
  const [languageSearchTerm, setLanguageSearchTerm] = useState('');
  const [selectedLanguageString, setSelectedLanguageString] = useState(null);
  const [editingLanguageString, setEditingLanguageString] = useState(false);
  const [newLanguageString, setNewLanguageString] = useState({
    string_id: '',
    pages: [],
    en: '',
    fr: '',
    es: '',
    de: '',
    nl: '',
    tr: ''
  });
  
  // Pagination
  const [nextToken, setNextToken] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'string_id', direction: 'ascending' });
  
  // Fetch language strings with pagination and search support
  const fetchLanguageStrings = async (searchTerm = '', token = null, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Prepare request parameters
      const params = {
        limit: 100
      };
      
      // Add search term if provided - specifically for string_id search
      if (searchTerm && searchTerm.trim() !== '') {
        params.search = searchTerm.trim();
      }
      
      // Add pagination token if provided
      if (token) {
        params.next_token = token;
      }
      
      // Make the API call
      const response = await settingsService.getLanguageStrings(params);
      
      // Extract language entries from the response
      const languageStringsData = response.language_entries || 
                                (response.data && response.data.language_entries) || 
                                [];
      
      // Store next token for pagination if available
      if (response.next_token) {
        setNextToken(response.next_token);
        setHasMoreData(true);
      } else {
        setNextToken(null);
        setHasMoreData(false);
      }
      
      // Update state based on whether we're appending or replacing
      if (append) {
        setLanguageStrings(prev => [...prev, ...languageStringsData]);
        setFilteredLanguageStrings(prev => [...prev, ...languageStringsData]);
      } else {
        setLanguageStrings(languageStringsData);
        setFilteredLanguageStrings(languageStringsData);
      }
    } catch (err) {
      console.error('Error fetching language strings:', err);
      setError(t('failed_to_load_language_strings'));
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Add debouncing to the search to improve performance
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Initial fetch
  useEffect(() => {
    fetchLanguageStrings();
  }, []);
  
  // Sort data when sortConfig changes
  useEffect(() => {
    if (filteredLanguageStrings.length > 0 && sortConfig) {
      const sortedData = [...filteredLanguageStrings].sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
      
      setFilteredLanguageStrings(sortedData);
    }
  }, [sortConfig]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleLanguageSearch = (searchTerm) => {
    setLanguageSearchTerm(searchTerm);
    
    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set a new timeout to debounce the search
    const timeout = setTimeout(() => {
      // Reset pagination when performing a new search
      setNextToken(null);
      setHasMoreData(true);
      fetchLanguageStrings(searchTerm);
    }, 300); // Wait 300ms after typing stops
    
    setSearchTimeout(timeout);
  };

  // Clean up the timeout when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleLoadMore = () => {
    if (nextToken && !isLoadingMore) {
      fetchLanguageStrings(languageSearchTerm, nextToken, true);
    }
  };

  const handleSelectLanguageString = (langString) => {
    setSelectedLanguageString(langString);
    setEditingLanguageString(false);
  };

  const handleCloseDetails = () => {
    setSelectedLanguageString(null);
    setEditingLanguageString(false);
  };

  const handleEditLanguageString = () => {
    if (selectedLanguageString) {
      setNewLanguageString({
        string_id: selectedLanguageString.string_id,
        pages: [...(selectedLanguageString.pages || [])],
        en: selectedLanguageString.en || '',
        fr: selectedLanguageString.fr || '',
        es: selectedLanguageString.es || '',
        de: selectedLanguageString.de || '',
        nl: selectedLanguageString.nl || '',
        tr: selectedLanguageString.tr || ''
      });
      setEditingLanguageString(true);
    }
  };

  const handleNewLanguageString = () => {
    setSelectedLanguageString(null);
    setNewLanguageString({
      string_id: '',
      pages: [],
      en: '',
      fr: '',
      es: '',
      de: '',
      nl: '',
      tr: ''
    });
    setEditingLanguageString(true);
  };

  const handleLanguageStringChange = (e) => {
    const { name, value } = e.target;
    setNewLanguageString(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLanguagePageChange = (e) => {
    const value = e.target.value;
    if (e.target.checked) {
      setNewLanguageString(prev => ({
        ...prev,
        pages: [...prev.pages, value]
      }));
    } else {
      setNewLanguageString(prev => ({
        ...prev,
        pages: prev.pages.filter(page => page !== value)
      }));
    }
  };

  // Handle saving language string with improved error handling
  const handleSaveLanguageString = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate required fields
      if (!newLanguageString.string_id || !newLanguageString.en) {
        throw new Error(t('string_id_and_english_required'));
      }
      
      let response;
      
      if (selectedLanguageString) {
        // Update existing string
        response = await settingsService.updateLanguageString(newLanguageString);
        setSuccess(t('language_string_updated_successfully'));
      } else {
        // Create new string
        response = await settingsService.createLanguageString(newLanguageString);
        setSuccess(t('language_string_created_successfully'));
      }
      
      // Close the edit form
      setEditingLanguageString(false);
      setSelectedLanguageString(null);
      
      // Wait a moment before refreshing the list to ensure the backend has processed the change
      setTimeout(async () => {
        try {
          // Refresh the language strings list with the current search term
          await fetchLanguageStrings(languageSearchTerm);
        } catch (refreshErr) {
          // Log the error but don't show it to the user since the main operation succeeded
          console.error('Error refreshing language strings:', refreshErr);
        }
      }, 1500); // Increased delay to give backend more time
    } catch (err) {
      console.error('Error saving language string:', err);
      setError(err.message || t('failed_to_save_language_string'));
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting language string
  const handleDeleteLanguageString = async () => {
    if (!selectedLanguageString) return;
    
    if (!window.confirm(t('confirm_delete_language_string'))) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await settingsService.deleteLanguageString(selectedLanguageString.string_id);
      
      // Wait a moment before refreshing the list
      setTimeout(async () => {
        try {
          // Refresh the language strings list
          await fetchLanguageStrings(languageSearchTerm);
          
          setSelectedLanguageString(null);
          setSuccess(t('language_string_deleted_successfully'));
        } catch (refreshErr) {
          console.error('Error refreshing language strings:', refreshErr);
        }
      }, 1000);
    } catch (err) {
      console.error('Error deleting language string:', err);
      setError(t('failed_to_delete_language_string'));
    } finally {
      setLoading(false);
    }
  };

  // Handle sort
  const handleSort = (columnId, direction) => {
    setSortConfig({ key: columnId, direction });
  };

  // Table columns definition
  const columns = [
    {
      id: 'string_id',
      header: t('string_id'),
      sortable: true,
      cell: (row) => (
        <div className={styles.stringIdCell}>
          {row.string_id}
        </div>
      )
    },
    {
      id: 'en',
      header: t('english'),
      sortable: true,
      cell: (row) => (
        <div className={styles.translationCell}>
          {row.en ? (
            row.en.length > 50 ? `${row.en.substring(0, 50)}...` : row.en
          ) : '-'}
        </div>
      )
    },
    {
      id: 'fr',
      header: t('french'),
      sortable: true,
      cell: (row) => (
        <div className={styles.translationCell}>
          {row.fr ? (
            row.fr.length > 50 ? `${row.fr.substring(0, 50)}...` : row.fr
          ) : '-'}
        </div>
      )
    },
    {
      id: 'es',
      header: t('spanish'),
      sortable: true,
      cell: (row) => (
        <div className={styles.translationCell}>
          {row.es ? (
            row.es.length > 50 ? `${row.es.substring(0, 50)}...` : row.es
          ) : '-'}
        </div>
      )
    },
    {
      id: 'de',
      header: t('german'),
      sortable: true,
      cell: (row) => (
        <div className={styles.translationCell}>
          {row.de ? (
            row.de.length > 50 ? `${row.de.substring(0, 50)}...` : row.de
          ) : '-'}
        </div>
      )
    },
    {
      id: 'nl',
      header: t('dutch'),
      sortable: true,
      cell: (row) => (
        <div className={styles.translationCell}>
          {row.nl ? (
            row.nl.length > 50 ? `${row.nl.substring(0, 50)}...` : row.nl
          ) : '-'}
        </div>
      )
    },
    {
      id: 'tr',
      header: t('turkish'),
      sortable: true,
      cell: (row) => (
        <div className={styles.translationCell}>
          {row.tr ? (
            row.tr.length > 50 ? `${row.tr.substring(0, 50)}...` : row.tr
          ) : '-'}
        </div>
      )
    },
    {
      id: 'updated_at',
      header: t('updated_at'),
      sortable: true,
      format: 'date',
      cell: (row) => <span>{formatDate(row.updated_at)}</span>
    }
  ];

  return (
    <div className={styles.languageTab}>
      <div className={styles.languageHeader}>
        <SuperAdminSearch
          placeholder={t('search_language_strings')}
          onSearch={handleLanguageSearch}
          initialValue={languageSearchTerm}
        />
        
        <button 
          className={styles.addButton}
          onClick={handleNewLanguageString}
          disabled={loading}
        >
          <i className="fas fa-plus"></i>
          <span>{t('add_language_string')}</span>
        </button>
      </div>
      
      <div className={styles.tableContainer}>
        <SuperAdminTableV2
          columns={columns}
          data={filteredLanguageStrings}
          isLoading={loading && !editingLanguageString && !isLoadingMore}
          emptyMessage={t('no_language_strings_found')}
          onRowClick={handleSelectLanguageString}
          sortConfig={sortConfig}
          onSort={handleSort}
          pageSize={100} // Show all loaded items, we're using server-side pagination
        />
        
        {hasMoreData && (
          <div className={styles.loadMoreContainer}>
            <button 
              className={styles.loadMoreButton}
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <div className={styles.spinner}></div>
                  <span>{t('loading_more')}</span>
                </>
              ) : (
                <span>{t('load_more')}</span>
              )}
            </button>
          </div>
        )}
      </div>
      
      {/* Overlay for language string details or editing */}
      {(selectedLanguageString || editingLanguageString) && (
        <div className={styles.overlay}>
          <div className={styles.overlayContent}>
            <div className={styles.overlayHeader}>
              <h2 className={styles.overlayTitle}>
                {editingLanguageString 
                  ? (selectedLanguageString ? t('edit_language_string') : t('add_language_string'))
                  : t('language_string_details')
                }
              </h2>
              <button className={styles.closeButton} onClick={handleCloseDetails}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className={styles.overlayBody}>
              {editingLanguageString ? (
                <div className={styles.languageForm}>
                  <div className={styles.formGroup}>
                    <label htmlFor="string_id" className={styles.formLabel}>
                      {t('string_id')} <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="string_id"
                      name="string_id"
                      className={styles.formInput}
                      value={newLanguageString.string_id}
                      onChange={handleLanguageStringChange}
                      disabled={loading || selectedLanguageString}
                      required
                    />
                    <div className={styles.formHelp}>{t('string_id_help')}</div>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('pages')}</label>
                    <div className={styles.checkboxGroup}>
                      {['home', 'login', 'signup', 'dashboard', 'settings', 'default'].map(page => (
                        <label key={page} className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            name="pages"
                            value={page}
                            checked={newLanguageString.pages.includes(page)}
                            onChange={handleLanguagePageChange}
                            disabled={loading}
                            className={styles.checkbox}
                          />
                          {page}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="en" className={styles.formLabel}>
                      {t('english')} (en) <span className={styles.required}>*</span>
                    </label>
                    <textarea
                      id="en"
                      name="en"
                      className={styles.formTextarea}
                      value={newLanguageString.en}
                      onChange={handleLanguageStringChange}
                      disabled={loading}
                      required
                      rows={4}
                    ></textarea>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="fr" className={styles.formLabel}>
                      {t('french')} (fr)
                    </label>
                    <textarea
                      id="fr"
                      name="fr"
                      className={styles.formTextarea}
                      value={newLanguageString.fr}
                      onChange={handleLanguageStringChange}
                      disabled={loading}
                      rows={4}
                    ></textarea>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="es" className={styles.formLabel}>
                      {t('spanish')} (es)
                    </label>
                    <textarea
                      id="es"
                      name="es"
                      className={styles.formTextarea}
                      value={newLanguageString.es}
                      onChange={handleLanguageStringChange}
                      disabled={loading}
                      rows={4}
                    ></textarea>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="de" className={styles.formLabel}>
                      {t('german')} (de)
                    </label>
                    <textarea
                      id="de"
                      name="de"
                      className={styles.formTextarea}
                      value={newLanguageString.de}
                      onChange={handleLanguageStringChange}
                      disabled={loading}
                      rows={4}
                    ></textarea>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="nl" className={styles.formLabel}>
                      {t('dutch')} (nl)
                    </label>
                    <textarea
                      id="nl"
                      name="nl"
                      className={styles.formTextarea}
                      value={newLanguageString.nl}
                      onChange={handleLanguageStringChange}
                      disabled={loading}
                      rows={4}
                    ></textarea>
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="tr" className={styles.formLabel}>
                      {t('turkish')} (tr)
                    </label>
                    <textarea
                      id="tr"
                      name="tr"
                      className={styles.formTextarea}
                      value={newLanguageString.tr}
                      onChange={handleLanguageStringChange}
                      disabled={loading}
                      rows={4}
                    ></textarea>
                  </div>
                  
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => setEditingLanguageString(false)}
                      disabled={loading}
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="button"
                      className={styles.saveButton}
                      onClick={handleSaveLanguageString}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className={styles.spinner}></div>
                          <span>{t('saving')}</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          <span>{t('save')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : selectedLanguageString ? (
                <div className={styles.languageDetails}>
                  <div className={styles.detailsActions}>
                    <button
                      className={styles.editButton}
                      onClick={handleEditLanguageString}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>{t('edit')}</span>
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={handleDeleteLanguageString}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                      <span>{t('delete')}</span>
                    </button>
                  </div>
                  
                  <div className={styles.detailsSection}>
                    <h3 className={styles.detailsSectionTitle}>{t('basic_information')}</h3>
                    <div className={styles.detailsGrid}>
                      <div className={styles.detailsItem}>
                        <div className={styles.detailsLabel}>{t('string_id')}</div>
                        <div className={styles.detailsValue}>{selectedLanguageString.string_id}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.detailsSection}>
                    <h3 className={styles.detailsSectionTitle}>{t('translations')}</h3>
                    <div className={styles.translationsList}>
                      <div className={styles.translationItem}>
                        <div className={styles.translationLanguage}>
                          <span className={styles.languageCode}>en</span>
                          <span className={styles.languageName}>{t('english')}</span>
                        </div>
                        <div className={styles.translationValue}>{selectedLanguageString.en || '-'}</div>
                      </div>
                      <div className={styles.translationItem}>
                        <div className={styles.translationLanguage}>
                          <span className={styles.languageCode}>fr</span>
                          <span className={styles.languageName}>{t('french')}</span>
                        </div>
                        <div className={styles.translationValue}>{selectedLanguageString.fr || '-'}</div>
                      </div>
                      <div className={styles.translationItem}>
                        <div className={styles.translationLanguage}>
                          <span className={styles.languageCode}>es</span>
                          <span className={styles.languageName}>{t('spanish')}</span>
                        </div>
                        <div className={styles.translationValue}>{selectedLanguageString.es || '-'}</div>
                      </div>
                      {selectedLanguageString.de && (
                        <div className={styles.translationItem}>
                          <div className={styles.translationLanguage}>
                            <span className={styles.languageCode}>de</span>
                            <span className={styles.languageName}>{t('german')}</span>
                          </div>
                          <div className={styles.translationValue}>{selectedLanguageString.de}</div>
                        </div>
                      )}
                      {selectedLanguageString.nl && (
                        <div className={styles.translationItem}>
                          <div className={styles.translationLanguage}>
                            <span className={styles.languageCode}>nl</span>
                            <span className={styles.languageName}>{t('dutch')}</span>
                          </div>
                          <div className={styles.translationValue}>{selectedLanguageString.nl}</div>
                        </div>
                      )}
                      {selectedLanguageString.tr && (
                        <div className={styles.translationItem}>
                          <div className={styles.translationLanguage}>
                            <span className={styles.languageCode}>tr</span>
                            <span className={styles.languageName}>{t('turkish')}</span>
                          </div>
                          <div className={styles.translationValue}>{selectedLanguageString.tr}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.detailsSection}>
                    <h3 className={styles.detailsSectionTitle}>{t('pages')}</h3>
                    <div className={styles.pagesList}>
                      {selectedLanguageString.pages && selectedLanguageString.pages.length > 0 ? (
                        selectedLanguageString.pages.map(page => (
                          <span key={page} className={styles.pageTag}>{page}</span>
                        ))
                      ) : (
                        <span className={styles.emptyText}>{t('no_pages_assigned')}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.detailsSection}>
                    <h3 className={styles.detailsSectionTitle}>{t('metadata')}</h3>
                    <div className={styles.detailsGrid}>
                      <div className={styles.detailsItem}>
                        <div className={styles.detailsLabel}>{t('created_at')}</div>
                        <div className={styles.detailsValue}>{formatDate(selectedLanguageString.created_at)}</div>
                      </div>
                      <div className={styles.detailsItem}>
                        <div className={styles.detailsLabel}>{t('updated_at')}</div>
                        <div className={styles.detailsValue}>{formatDate(selectedLanguageString.updated_at)}</div>
                      </div>
                      {selectedLanguageString.created_by && (
                        <div className={styles.detailsItem}>
                          <div className={styles.detailsLabel}>{t('created_by')}</div>
                          <div className={styles.detailsValue}>{selectedLanguageString.created_by}</div>
                        </div>
                      )}
                      {selectedLanguageString.updated_by && (
                        <div className={styles.detailsItem}>
                          <div className={styles.detailsLabel}>{t('updated_by')}</div>
                          <div className={styles.detailsValue}>{selectedLanguageString.updated_by}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageManagement;